/**
 * Modal that collects a fresh TOTP code for sensitive platform-owner
 * actions. The caller provides ``onConfirm(code)`` and the modal
 * closes after the call resolves; on a 401/403 it surfaces the error
 * and lets the user try again with a fresh code (the timestep
 * advances every 30 seconds).
 */

import { type FormEvent, useState } from "react";

export function MfaPrompt({
  open,
  title,
  description,
  onCancel,
  onConfirm,
}: {
  open: boolean;
  title: string;
  description: string;
  onCancel: () => void;
  onConfirm: (code: string) => Promise<void>;
}) {
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!open) {
    return null;
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await onConfirm(code);
      setCode("");
    } catch (exc) {
      const message = exc instanceof Error
        ? exc.message
        : (exc as { message?: string }).message ?? "Verification failed.";
      setError(message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="sheet-backdrop" role="dialog" aria-modal="true" onClick={onCancel}>
      <div
        className="sheet"
        style={{ maxWidth: 360 }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sheet-head">
          <div className="sheet-title">{title}</div>
        </div>
        <div className="sheet-body">
          <form onSubmit={onSubmit} noValidate>
            <p style={{ fontSize: 13, color: "var(--text-muted, #555)", marginTop: 0 }}>
              {description}
            </p>
            <div className="field">
              <label htmlFor="mfa-prompt-code">6-digit authenticator code</label>
              <input
                id="mfa-prompt-code"
                type="text"
                inputMode="numeric"
                pattern="[0-9]{6}"
                autoComplete="one-time-code"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                autoFocus
                required
              />
            </div>
            {error !== null && (
              <div className="auth-error" role="alert">
                {error}
              </div>
            )}
            <div className="actions">
              <button
                type="submit"
                className="btn btn-primary btn-sm"
                disabled={busy}
              >
                {busy ? "Verifying..." : "Confirm"}
              </button>
              <button
                type="button"
                className="btn btn-sm"
                onClick={onCancel}
                disabled={busy}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
